/**
 * A club's logo („Vereinslogo“), one per tenant and optional (owner,
 * 13.10.2026: „Der Administrator eines Vereins sollte in der Lage sein, ein
 * Vereinslogo hochzuladen, welches dann auch bei der Auswahl eines Vereins …
 * zu sehen ist“).
 *
 * PNG, not JPEG like the profile picture: logos are drawn on a transparent
 * background, and a JPEG would put them on a white box. The web draws the
 * chosen square (`logoCropRect`, by default the whole picture) into a
 * LOGO_EDGE square and encodes PNG, then drops
 * every ancillary chunk that is not about colour (`stripPngMetadata` — text,
 * time, EXIF and the like). The API checks the result with `inspectLogo`:
 * a well-formed PNG whose chunks are all on the list, every CRC right, within
 * the size limits. Without a logo every client shows the club's code.
 */
import { crc32, inspectPng } from './png';

/** Side of the square the web encodes (the chosen section, transparent where the picture does not reach). Sharp at 48 pt on a 3x screen. */
export const LOGO_EDGE = 256;
/** Upper bound the API accepts. 256 × 256 RGBA is 262 KB before deflate; real logos are 5–60 KB. */
export const LOGO_MAX_BYTES = 300_000;
export const LOGO_MIN_EDGE = 32;
export const LOGO_MAX_EDGE = 512;

/**
 * The chunks a logo may carry: the critical ones and those about colour and
 * transparency. Everything else — tEXt, iTXt, zTXt, tIME, eXIf, iCCP (an ICC
 * profile is free-form data) and private chunks — is dropped by the clients
 * and refused by the API.
 */
export const LOGO_CHUNKS: readonly string[] = ['IHDR', 'PLTE', 'tRNS', 'IDAT', 'IEND', 'sRGB', 'gAMA', 'cHRM', 'pHYs', 'sBIT', 'bKGD'];

export type LogoRefusal = 'not_png' | 'too_large' | 'size' | 'metadata';

export type LogoVerdict =
  | { readonly ok: true; readonly width: number; readonly height: number }
  | { readonly ok: false; readonly reason: LogoRefusal };

const SIGNATURE_BYTES = 8;

function u32(bytes: Uint8Array, at: number): number {
  return ((bytes[at]! << 24) | (bytes[at + 1]! << 16) | (bytes[at + 2]! << 8) | bytes[at + 3]!) >>> 0;
}

interface Chunk {
  readonly type: string;
  readonly start: number;
  readonly end: number;
}

/** Every chunk after the signature, or null if one runs past the end, has a wrong CRC, or something follows IEND. */
function chunksOf(bytes: Uint8Array): Chunk[] | null {
  const out: Chunk[] = [];
  let at = SIGNATURE_BYTES;
  while (at < bytes.length) {
    if (at + 12 > bytes.length) return null;
    const length = u32(bytes, at);
    const end = at + 12 + length;
    if (end > bytes.length) return null;
    const type = String.fromCharCode(...bytes.subarray(at + 4, at + 8));
    if (!/^[A-Za-z]{4}$/.test(type)) return null;
    if (crc32(bytes.subarray(at + 4, at + 8 + length)) !== u32(bytes, at + 8 + length)) return null;
    out.push({ type, start: at, end });
    at = end;
    if (type === 'IEND') return at === bytes.length ? out : null;
  }
  return null;
}

/** The server's rule for an uploaded logo: PNG, only listed chunks, every CRC right, ≤ LOGO_MAX_BYTES, longer edge within LOGO_MIN_EDGE…LOGO_MAX_EDGE. */
export function inspectLogo(bytes: Uint8Array): LogoVerdict {
  if (bytes.length > LOGO_MAX_BYTES) return { ok: false, reason: 'too_large' };
  const png = inspectPng(bytes);
  if (!png.ok) return { ok: false, reason: 'not_png' };
  const chunks = chunksOf(bytes);
  if (!chunks || !chunks.some((c) => c.type === 'IDAT')) return { ok: false, reason: 'not_png' };
  if (chunks.some((c) => !LOGO_CHUNKS.includes(c.type))) return { ok: false, reason: 'metadata' };
  const long = Math.max(png.width, png.height);
  if (long < LOGO_MIN_EDGE || long > LOGO_MAX_EDGE) return { ok: false, reason: 'size' };
  return { ok: true, width: png.width, height: png.height };
}

/**
 * The PNG without the chunks `inspectLogo` refuses. Only ancillary chunks
 * (lower-case first letter) can be dropped; a PNG with an unknown critical
 * chunk, or a malformed one, comes back unchanged and is refused later.
 */
export function stripPngMetadata(bytes: Uint8Array): Uint8Array {
  const chunks = chunksOf(bytes);
  if (!chunks) return bytes;
  const keep = chunks.filter((c) => LOGO_CHUNKS.includes(c.type) || !/^[a-z]/.test(c.type));
  if (keep.length === chunks.length) return bytes;
  const out = new Uint8Array(SIGNATURE_BYTES + keep.reduce((n, c) => n + c.end - c.start, 0));
  out.set(bytes.subarray(0, SIGNATURE_BYTES));
  let at = SIGNATURE_BYTES;
  for (const c of keep) {
    out.set(bytes.subarray(c.start, c.end), at);
    at += c.end - c.start;
  }
  return out;
}


/**
 * The section of a logo the admin chose (owner, 13.10.2026: „beim Hochladen
 * eines Vereinslogos einen Ausschnitt wählen … default … komplett in den
 * verfügbaren Platz eingefügt“). Unlike the profile picture, zoom 1 is the
 * WHOLE picture inside the square — its longer edge is the square's side, the
 * rest stays transparent. Zooming in cuts; the centre (`x`, `y`, image pixels)
 * stays on the picture, so it can never be pushed out of the square entirely.
 */
export interface LogoView {
  readonly zoom: number;
  readonly x: number;
  readonly y: number;
}

/** How far in: an eighth of the longer edge, enough to cut a crest out of a letterhead. */
export const LOGO_MAX_ZOOM = 8;

type LogoSize = { readonly width: number; readonly height: number };

/** The whole picture, centred: the default, and what an upload without a crop gets. */
export function initialLogoView(image: LogoSize): LogoView {
  return { zoom: 1, x: image.width / 2, y: image.height / 2 };
}

/** Zoom within 1…LOGO_MAX_ZOOM; at zoom 1 the picture stays centred, beyond it the centre stays on the picture. */
export function clampLogoView(image: LogoSize, view: LogoView): LogoView {
  const zoom = Math.min(LOGO_MAX_ZOOM, Math.max(1, Number.isFinite(view.zoom) ? view.zoom : 1));
  if (zoom === 1) return initialLogoView(image);
  const clamp = (v: number, max: number) => Math.min(max, Math.max(0, Number.isFinite(v) ? v : max / 2));
  return { zoom, x: clamp(view.x, image.width), y: clamp(view.y, image.height) };
}

/** Dragging by (dx, dy) screen pixels in a viewport of `viewport` pixels moves the section the other way. */
export function panLogoView(image: LogoSize, view: LogoView, dx: number, dy: number, viewport: number): LogoView {
  const perPixel = Math.max(image.width, image.height) / view.zoom / viewport;
  return clampLogoView(image, { ...view, x: view.x - dx * perPixel, y: view.y - dy * perPixel });
}

/**
 * The square to draw into the logo, as fractions of the picture's longer edge
 * (it may reach beyond the picture: that part stays transparent). Fractions,
 * not pixels, because an SVG is measured differently by the crop dialog's
 * <img> and by the canvas that draws it.
 */
export function logoCropRect(image: LogoSize, view: LogoView): { x: number; y: number; side: number } {
  const v = clampLogoView(image, view);
  const long = Math.max(image.width, image.height);
  const side = long / v.zoom;
  return { x: (v.x - side / 2) / long, y: (v.y - side / 2) / long, side: side / long };
}
